import { useEffect, useRef } from 'react';

import { WIZARD_TARGET_WAIT_TIMEOUT_MS } from './constants';
import type { WizardDefinition } from './types';
import { queryWizardTarget } from './targetUtils';
import { useWizard } from './WizardProvider';
import { useWizardsAllowed } from './wizardViewport';

export function useStartWizardWhenTargetReady({
  enabled,
  firstStepTargetId,
  definition,
  targetWaitTimeoutMs = WIZARD_TARGET_WAIT_TIMEOUT_MS,
}: {
  enabled: boolean;
  firstStepTargetId: string;
  definition: WizardDefinition;
  targetWaitTimeoutMs?: number;
}): void {
  const { activeWizard, startWizard } = useWizard();
  const wizardsAllowed = useWizardsAllowed();
  const hasStartedRef = useRef(false);
  const canStart = enabled && wizardsAllowed;

  useEffect(() => {
    if (!canStart) {
      hasStartedRef.current = false;
      return;
    }

    if (hasStartedRef.current || activeWizard) {
      return;
    }

    const tryStartWizard = () => {
      if (!queryWizardTarget(firstStepTargetId)) {
        return false;
      }

      hasStartedRef.current = true;
      startWizard(definition);
      return true;
    };

    if (tryStartWizard()) {
      return;
    }

    const observer = new MutationObserver(() => {
      if (tryStartWizard()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const timeoutId = window.setTimeout(() => {
      observer.disconnect();
    }, targetWaitTimeoutMs);

    return () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [
    activeWizard,
    canStart,
    definition,
    firstStepTargetId,
    startWizard,
    targetWaitTimeoutMs,
  ]);
}
